import React, { useState, memo } from 'react';
import DashboardLayout from '../../components/DashboardLayout/DashboardLayout';
import { BrowserView, MobileView } from 'react-device-detect';
import API from '../../utils/API';
import formatLink from '../../utils/formatLink';
import { useSelector, useDispatch } from 'react-redux';
import { saveOrg } from '../../redux/actions';
import './managewishlist-page.css';
import { Modal } from 'react-bootstrap';
import MobileTopBar from '../../components/common/MobileTopBar';

const SAMPLE_HINT = 'http://www.amazon.com/mynonprofitwishlist';

/**
 * HeaderBlock: stable component (defined OUTSIDE the page component)
 */
const HeaderBlock: React.FC<{ reduxOrg: any }> = ({ reduxOrg }) => (
  <>
    <h1 className="manage-wishlist-page-h1">Wishlist</h1>
    {/* <p className="store-hint"></p> */}
    <p className="wishlist-hint">{reduxOrg?.actionItems?.wishlist || SAMPLE_HINT}</p>

  </>
);

/**
 * FormAndCopy: stable + memoized so it won't remount unless props actually change.
 * Note: we pass only the minimal props it needs.
 */
type FormProps = {
  reduxOrg: any;
  input: { _id?: string; wishlist?: string };
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e?: any) => Promise<void> | void;
  submitting: boolean;
};

const FormAndCopy: React.FC<FormProps> = memo(
  ({ reduxOrg, input, handleChange, handleSubmit, submitting }) => {
    return (
      <div className="wishlist-two-col">
        <div className="wishlist-left">
          <label htmlFor="wishlist" className="form-label">
            Wishlist Link
          </label>
          <input
            id="wishlist"
            type="text"
            name="wishlist"
            placeholder="Paste your wishlist link here"
            onChange={handleChange}
            required
            value={input.wishlist || ''}
            className="wishlist-input"
            autoComplete="off"
          />
          <button
            type="button"
            className="wishlist-submit"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>

        <div className="wishlist-right">
          <h3>What’s a Wishlist For?</h3>
          <p>
            At NEDDIE, nonprofits can share a wishlist of needed items,
            whether from Amazon, Walmart, or other retailers. It’s a simple way
            for donors to give tangible support by purchasing exactly what an
            organization needs. From supplies to equipment, every item helps move
            the mission forward.
          </p>
        </div>
      </div>
    );
  }
);

FormAndCopy.displayName = 'FormAndCopy';

const ManageWishlistPage = () => {
  const reduxOrg = useSelector((state: any) => state.org);
  const [input, setInput] = useState({ _id: reduxOrg?._id, wishlist: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showStoreSubmitModal, setShowStoreSubmitModal] = useState(false);
  const dispatch = useDispatch();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInput((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (event?: any) => {
    if (event?.preventDefault) event.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    const payload = { ...input, wishlist: formatLink(input.wishlist) };
    try {
      const response: any = await API.saveWishlist(payload);
      if (response?.status === 200) {
        const newReduxOrg = { ...reduxOrg };
        newReduxOrg.actionItems = {
          ...newReduxOrg.actionItems,
          wishlist: payload.wishlist,
        };
        dispatch(saveOrg(newReduxOrg));
        // optional: keep input synced with saved wishlist
        setShowStoreSubmitModal(true)
        setInput((p) => ({ ...p, wishlist: payload.wishlist }));
      }
    } catch (err) {
      console.log(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Render the layout once; render FormAndCopy only once (not twice)
  return (
    <>
      {/* Desktop */}
      <BrowserView style={{ height: '100%' }}>
        <DashboardLayout>
          <div className="managewishlist-container">
            <div className="manage-wishlist-wrap">
              <HeaderBlock reduxOrg={reduxOrg} />
              <FormAndCopy
                reduxOrg={reduxOrg}
                input={input}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                submitting={submitting}
              />
            </div>
          </div>
        </DashboardLayout>
      </BrowserView>

      {/* Mobile */}
      <MobileView>
        <MobileTopBar />
        <div className="managewishlist-mobile-container">
          <div className="manage-wishlist-wrap">
            {/* <HeaderBlock reduxOrg={reduxOrg} /> */}
            <FormAndCopy
              reduxOrg={reduxOrg}
              input={input}
              handleChange={handleChange}
              handleSubmit={handleSubmit}
              submitting={submitting}
            />
          </div>
        </div>
      </MobileView>
      <Modal
        show={showStoreSubmitModal}
        onHide={() => setShowStoreSubmitModal(false)}
        size="sm"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p style={{ fontSize: '16px', color: 'green' }}>
              Wishlist link saved successfully!
            </p>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ManageWishlistPage;
